import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Heart, Bookmark, MessageCircle, Flag } from "lucide-react";
import { toast } from "sonner";
import { PostTypeBadge, AccountBadge } from "../components/Badges";
import Seo from "../components/Seo";

const TYPES = ["all","inspiration","aftercare","flash","general"];

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ post_type: "general", body: "", image_url: "" });
  const [open, setOpen] = useState({});
  const [replies, setReplies] = useState({});

  const load = () => api.get("/community/posts", { params: filter !== "all" ? { post_type: filter } : {} }).then((r) => setPosts(r.data));
  useEffect(() => { load(); }, [filter]);

  const post = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to post"); return; }
    try { await api.post("/community/posts", form); setForm({ post_type: "general", body: "", image_url: "" }); load(); }
    catch { toast.error("Failed"); }
  };
  const like = async (id) => { if (!user) return toast.error("Sign in"); await api.post(`/community/posts/${id}/like`); load(); };
  const save = async (id) => { if (!user) return toast.error("Sign in"); await api.post(`/community/posts/${id}/save`); toast.success("Saved"); };
  const flag = async (id) => { if (!user) return toast.error("Sign in"); await api.post("/flags", { target_type: "community_post", target_id: id, reason: "" }); toast.success("Reported. Thanks."); };
  const toggleReplies = async (id) => {
    setOpen({ ...open, [id]: !open[id] });
    if (!replies[id]) { const r = await api.get(`/community/posts/${id}/replies`); setReplies({ ...replies, [id]: r.data }); }
  };
  const submitReply = async (id, body) => {
    if (!user) return toast.error("Sign in");
    const r = await api.post(`/community/posts/${id}/replies`, { body });
    setReplies({ ...replies, [id]: [...(replies[id] || []), r.data] });
    load();
  };

  return (
    <div className="container-x py-12" data-testid="community-page">
      <Seo title="Community" description="Join the Inkstead community: share ideas, ask questions and connect with tattoo artists and collectors."/>
      <h1 className="font-serif text-4xl md:text-5xl">Community</h1>
      <p className="text-black/60 mt-2">Show inspiration, ask aftercare, drop flash news.</p>

      {user && (
        <form onSubmit={post} className="card mt-8" data-testid="community-new-post">
          <div className="flex gap-2 flex-wrap mb-3">
            {["general","inspiration","aftercare","flash"].map((t) => (
              <button type="button" key={t} onClick={() => setForm({ ...form, post_type: t })}
                className={`badge ${form.post_type === t ? "badge-flash" : "badge-neutral"}`} data-testid={`new-type-${t}`}>{t}</button>
            ))}
          </div>
          <textarea required rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Share something..." className="input-field" data-testid="community-new-body"/>
          <div className="flex justify-between items-center mt-3">
            <input value={form.image_url} onChange={(e)=>setForm({...form, image_url: e.target.value})} placeholder="Image URL (optional)" className="input-field max-w-xs" data-testid="community-new-img"/>
            <button className="btn-filled" data-testid="community-submit">Post</button>
          </div>
        </form>
      )}

      <div className="mt-8 flex gap-2 flex-wrap">
        {TYPES.map((t) => <button key={t} onClick={()=>setFilter(t)} className={`badge ${filter===t?"badge-flash":"badge-neutral"}`} data-testid={`filter-${t}`}>{t}</button>)}
      </div>

      <div className="mt-6 space-y-4">
        {posts.map((p) => (
          <div key={p.id} className="card" data-testid={`cpost-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium">{p.author_name}</span>
                <AccountBadge type={p.author_account_type}/>
                <span className="text-xs text-black/40">· {new Date(p.created_at).toLocaleDateString()}</span>
              </div>
              <PostTypeBadge type={p.post_type}/>
            </div>
            <p className="mt-3 text-black/80 whitespace-pre-wrap">{p.body}</p>
            {p.image_url && <img src={p.image_url} alt="" className="mt-3 rounded-[8px] max-h-96 object-cover w-full"/>}
            <div className="flex gap-4 mt-4 text-sm">
              <button onClick={()=>like(p.id)} className="flex items-center gap-1 hover:text-black text-black/70" data-testid={`like-${p.id}`}><Heart size={14} fill={user && p.likes?.includes(user.id) ? "#000": "none"}/> {p.likes?.length || 0}</button>
              <button onClick={()=>toggleReplies(p.id)} className="flex items-center gap-1 text-black/70 hover:text-black" data-testid={`replies-${p.id}`}><MessageCircle size={14}/> {p.reply_count}</button>
              <button onClick={()=>save(p.id)} className="flex items-center gap-1 text-black/70 hover:text-black"><Bookmark size={14}/></button>
              <button onClick={()=>flag(p.id)} className="flex items-center gap-1 text-black/70 hover:text-black ml-auto" data-testid={`flag-${p.id}`}><Flag size={12}/> Report</button>
            </div>
            {open[p.id] && (
              <div className="mt-4 border-t-[0.5px] border-black/10 pt-4 space-y-3">
                {(replies[p.id] || []).map((r) => (
                  <div key={r.id} className="text-sm">
                    <div className="font-medium">{r.author_name} <span className="text-black/40 text-xs">· {new Date(r.created_at).toLocaleDateString()}</span></div>
                    <div className="text-black/80">{r.body}</div>
                  </div>
                ))}
                {user && <ReplyBox onSubmit={(b)=>submitReply(p.id, b)}/>}
              </div>
            )}
          </div>
        ))}
        {posts.length === 0 && <p className="text-black/50">Quiet here. Be the first.</p>}
      </div>
    </div>
  );
}

function ReplyBox({ onSubmit }) {
  const [v, setV] = useState("");
  return (
    <form onSubmit={(e)=>{e.preventDefault(); if (v.trim()) { onSubmit(v); setV(""); }}} className="flex gap-2">
      <input value={v} onChange={(e)=>setV(e.target.value)} placeholder="Reply..." className="input-field" data-testid="reply-input"/>
      <button className="btn-filled" data-testid="reply-submit">Send</button>
    </form>
  );
}
